import React, { useMemo, useState } from 'react';
import { checklistTemplateStrings, moduleCommonStrings } from '../../content';
import { useMutation, useQuery } from '@apollo/client';
import { ADD_CHECKLIST_TEMPLATE, DELETE_CHECKLIST_TEMPLATE, GET_CHECKLIST_TEMPLATES } from './operations';

/**
 * Normalizes title.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function normalizeTitle(value) {
  return String(value || '').trim().toLowerCase();
}

/**
 * Renders the checklist template picker experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.templates - templates value.
 * @param {*} props.isLoading - Whether is loading is enabled.
 * @param {*} props.onApply - Callback invoked when apply occurs.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @param {*} props.onDelete - Callback invoked when delete occurs.
 * @param {*} props.deletingId - deleting id value.
 * @param {*} props.hobbyName - hobby name value.
 * @param {*} props.errorMessage - error message value.
 * @returns {JSX.Element} The rendered interface.
 */
function ChecklistTemplatePicker({ templates, isLoading, onApply, onClose, onDelete, deletingId, hobbyName, errorMessage }) {
  const [selectedId, setSelectedId] = useState(templates[0]?.id || '');
  const [activeScope, setActiveScope] = useState('');
  const visibleTemplates = activeScope
    ? templates.filter((template) => template.scope === activeScope)
    : templates;
  const selectedTemplate = visibleTemplates.find((template) => template.id === selectedId) || visibleTemplates[0];
  const saved = templates.filter((template) => template.source === 'user');
  const visibleSaved = visibleTemplates.filter((template) => template.source === 'user');
  const visibleSuggested = visibleTemplates.filter((template) => template.source === 'default');
  const scopeOptions = [
    { value: '', label: checklistTemplateStrings.picker.allCategories },
    { value: 'hobby', label: checklistTemplateStrings.picker.thisHobby(hobbyName) },
    { value: 'checklist_hobbies', label: checklistTemplateStrings.picker.shared },
  ].filter((option) => !option.value || templates.some((template) => template.scope === option.value));

  /**
   * Performs the template scope label operation.
   *
   * @param {*} template - template value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const templateScopeLabel = (template) => (
    template.scope === 'checklist_hobbies'
      ? checklistTemplateStrings.picker.checklistLabel(checklistTemplateStrings.picker.shared)
      : checklistTemplateStrings.picker.checklistLabel(checklistTemplateStrings.picker.thisHobby(hobbyName))
  );

  /**
   * Renders template button.
   *
   * @param {*} template - template value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const renderTemplateButton = (template) => (
    <div
      key={template.id}
      className={`checklist-template-option ${selectedTemplate?.id === template.id ? 'active' : ''}`}
      onClick={() => setSelectedId(template.id)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setSelectedId(template.id);
      }}
      role="button"
      tabIndex={0}
    >
      <span className="checklist-template-copy">
        <strong>{template.name}</strong>
        <span>{[templateScopeLabel(template), checklistTemplateStrings.counts.items(template.items.length)].filter(Boolean).join(' • ')}</span>
      </span>
      {template.editable && (
        <button
          type="button"
          className="checklist-template-delete"
          aria-label={checklistTemplateStrings.picker.delete}
          onClick={(event) => {
            event.stopPropagation();
            onDelete(template);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            onDelete(template);
          }}
        >
          {deletingId === template.id ? checklistTemplateStrings.picker.deleting : 'Delete'}
        </button>
      )}
    </div>
  );

  return (
    <div className="modal-backdrop">
      <div className="modal-card checklist-template-modal">
        <div className="modal-header">
          <div>
            <p className="eyebrow">{checklistTemplateStrings.picker.eyebrow}</p>
            <h3>{checklistTemplateStrings.picker.title}</h3>
            {checklistTemplateStrings.picker.detail && <p>{checklistTemplateStrings.picker.detail}</p>}
          </div>
          <button type="button" className="btn-ghost" onClick={onClose}>{moduleCommonStrings.actions.cancel}</button>
        </div>

        {isLoading ? (
          <p className="detail-notes">{checklistTemplateStrings.picker.loading}</p>
        ) : (
          <div className="checklist-template-layout">
            <div className="checklist-template-list">
              {scopeOptions.length > 2 && (
                <div className="checklist-category-filter" role="group" aria-label="Checklist category">
                  {scopeOptions.map((option) => (
                    <button type="button" key={option.value || 'all'} className={activeScope === option.value ? 'active' : ''} onClick={() => setActiveScope(option.value)}>
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
              {saved.length > 0 && <p className="eyebrow">{checklistTemplateStrings.picker.saved}</p>}
              {visibleSaved.map(renderTemplateButton)}
              <p className="eyebrow">{checklistTemplateStrings.picker.suggested}</p>
              {visibleSuggested.map(renderTemplateButton)}
            </div>

            <div className="checklist-template-preview">
              {selectedTemplate ? (
                <>
                  <h4>{selectedTemplate.name}</h4>
                  <ul>
                    {selectedTemplate.items.map((item) => (
                      <li key={item.id}>
                        <span>{item.title}</span>
                        {item.category && <small>{item.category}</small>}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="detail-notes">{checklistTemplateStrings.picker.empty}</p>
              )}
            </div>
          </div>
        )}
        {errorMessage && <p className="field-error">{errorMessage}</p>}
        {!isLoading && selectedTemplate && (
          <div className="modal-actions sticky-modal-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>{moduleCommonStrings.actions.cancel}</button>
            <button type="button" className="btn-primary" onClick={() => onApply(selectedTemplate)}>
              {checklistTemplateStrings.picker.add(selectedTemplate.name)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Renders the save checklist modal experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.defaultName - default name value.
 * @param {*} props.itemCount - item count numeric value.
 * @param {*} props.onSave - Callback invoked when save occurs.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @param {*} props.isSaving - Whether is saving is enabled.
 * @param {*} props.hobbyName - hobby name value.
 * @param {*} props.errorMessage - error message value.
 * @returns {JSX.Element} The rendered interface.
 */
function SaveChecklistModal({ defaultName, itemCount, onSave, onClose, isSaving, hobbyName, errorMessage }) {
  const [name, setName] = useState(defaultName);
  const [scope, setScope] = useState('hobby');

  return (
    <div className="modal-backdrop">
      <div className="modal-card checklist-save-modal">
        <div className="modal-header">
          <div>
            <p className="eyebrow">{checklistTemplateStrings.save.eyebrow}</p>
            <h3>{checklistTemplateStrings.save.title}</h3>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>x</button>
        </div>
        <div className="form-group">
          <label>{checklistTemplateStrings.save.name}</label>
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder={defaultName || checklistTemplateStrings.save.placeholder} />
        </div>
        <div className="form-group">
          <label>{checklistTemplateStrings.save.scope}</label>
          <div className="collection-mode-toggle" role="group" aria-label={checklistTemplateStrings.save.scope}>
            <button type="button" className={scope === 'hobby' ? 'active' : ''} onClick={() => setScope('hobby')}>
              {checklistTemplateStrings.save.thisHobby(hobbyName)}
            </button>
            <button type="button" className={scope === 'checklist_hobbies' ? 'active' : ''} onClick={() => setScope('checklist_hobbies')}>
              {checklistTemplateStrings.save.shared}
            </button>
          </div>
        </div>
        <p className="detail-notes">{checklistTemplateStrings.save.detail(itemCount)}</p>
        {errorMessage && <p className="field-error">{errorMessage}</p>}
        <div className="modal-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>{moduleCommonStrings.actions.cancel}</button>
          <button type="button" className="btn-primary" disabled={isSaving || !name.trim()} onClick={() => onSave(name.trim(), scope)}>
            {isSaving ? checklistTemplateStrings.save.saving : checklistTemplateStrings.save.action}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Renders the checklist template tools experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.hobbyId - hobby id value.
 * @param {*} props.hobbyName - hobby name value.
 * @param {*} props.currentItems - current items collection.
 * @param {*} props.defaultName - default name value.
 * @param {*} props.onApplyItems - Callback invoked when apply items occurs.
 * @param {*} props.buildTemplateItems - build template items collection.
 * @returns {JSX.Element} The rendered interface.
 */
export default function ChecklistTemplateTools({
  hobbyId,
  hobbyName,
  currentItems,
  defaultName,
  onApplyItems,
  buildTemplateItems,
}) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const { data, loading } = useQuery(GET_CHECKLIST_TEMPLATES, {
    variables: { hobbyId },
    skip: !isPickerOpen,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [addTemplate, { loading: isSaving }] = useMutation(ADD_CHECKLIST_TEMPLATE, {
    refetchQueries: [{ query: GET_CHECKLIST_TEMPLATES, variables: { hobbyId } }],
  });
  const [deleteTemplate] = useMutation(DELETE_CHECKLIST_TEMPLATE, {
    refetchQueries: [{ query: GET_CHECKLIST_TEMPLATES, variables: { hobbyId } }],
  });

  const templates = data?.getChecklistTemplates || [];
  const savableItems = useMemo(
    () => (currentItems || []).filter((item) => normalizeTitle(item.title)),
    [currentItems]
  );

  /**
   * Handles apply.
   *
   * @param {*} template - template value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleApply = (template) => {
    onApplyItems(template.items || []);
    setIsPickerOpen(false);
  };

  /**
   * Handles save.
   *
   * @param {string} name - name value.
   * @param {*} scope - scope value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSave = async (name, scope) => {
    const items = buildTemplateItems(savableItems);
    if (!items.length) return;
    if (templates.some((template) => template.source === 'user' && normalizeTitle(template.name) === normalizeTitle(name))) {
      setTemplateError(checklistTemplateStrings.save.duplicateName);
      return;
    }
    try {
      setTemplateError('');
      await addTemplate({ variables: { input: { hobbyId, name, scope, items } } });
      setIsSaveOpen(false);
    } catch (error) {
      setTemplateError(error.message || 'Something went wrong. Please try again.');
    }
  };

  /**
   * Handles delete.
   *
   * @param {*} template - template value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleDelete = async (template) => {
    if (!template?.editable) return;
    if (!window.confirm(checklistTemplateStrings.picker.deleteConfirm(template.name))) return;
    try {
      setTemplateError('');
      setDeletingId(template.id);
      await deleteTemplate({ variables: { id: template.id } });
    } catch (error) {
      setTemplateError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <>
      <div className="checklist-template-actions">
        <button type="button" className="btn-secondary" onClick={() => {
          setTemplateError('');
          setIsPickerOpen(true);
        }}>{checklistTemplateStrings.actions.use}</button>
        <button type="button" className="btn-ghost" onClick={() => {
          setTemplateError('');
          setIsSaveOpen(true);
        }} disabled={savableItems.length === 0}>
          {checklistTemplateStrings.actions.saveAsReusable}
        </button>
      </div>
      {isPickerOpen && (
        <ChecklistTemplatePicker
          templates={templates}
          isLoading={loading}
          onApply={handleApply}
          onClose={() => setIsPickerOpen(false)}
          onDelete={handleDelete}
          deletingId={deletingId}
          hobbyName={hobbyName}
          errorMessage={templateError}
        />
      )}
      {isSaveOpen && (
        <SaveChecklistModal
          defaultName={defaultName}
          itemCount={savableItems.length}
          onSave={handleSave}
          onClose={() => setIsSaveOpen(false)}
          isSaving={isSaving}
          hobbyName={hobbyName}
          errorMessage={templateError}
        />
      )}
    </>
  );
}
